import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';

import { InstallationFlowType } from '../../constants/app.constants';
import { FormTypesData } from '../../domains/common.domain';
import { CommunicationService } from '../../services/communication.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { UrlSegments } from '../jurisdiction.constants';
import { ComponentFormData } from '../jurisdiction.domain';
import {
  FormSubsectionsVisibility,
  GasGridConnectionData,
  GetGridConnectionObject,
  GridConnectionData,
  HeatingGridConnectionData,
  PowerGridConnectionData,
  PowerSpecialConsumerData,
  WaterGridConnectionData,
} from './technician-data.domain';
import { EnvironmentService } from '../../services/environment.service';
import { JsonPipe } from '@angular/common';

@Injectable()
export class TechnicianDataService {
  private triggerComponentFormUpdate$: Subject<ComponentFormData>;
  private triggerActionOnFormSubmit$: Subject<boolean>;
  private triggerSpecialConsumersUpdate$: Subject<ComponentFormData>;
  private noOfSpecialDevices: number;

  private readonly currentFlowType: string;
  private gridConnectionApplicationObject: GridConnectionData;
  private selectedFacilities: FormSubsectionsVisibility;
  private selectedHousingType: FormTypesData[];

  private baseData: any;
  private feedInData: any;
  private gasConnectionData: any;
  private heatingConnectionData: any;
  private powerConnectionData: any;
  private waterConnectionData: any;

  constructor(
    private communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService
  ) {
    this.noOfSpecialDevices = 0;
    this.baseData = {};
    this.gasConnectionData = null;
    this.heatingConnectionData = null;
    this.powerConnectionData = null;
    this.waterConnectionData = null;
    this.selectedFacilities = {
      gas: false,
      heating: false,
      power: false,
      water: false,
    };
    this.selectedHousingType = [];
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.initializeFormSubscriptions();
  }

  get registerForComponentFormUpdate(): Subject<ComponentFormData> {
    return this.triggerComponentFormUpdate$;
  }

  get registerForActionOnFormSubmit(): Subject<boolean> {
    return this.triggerActionOnFormSubmit$;
  }

  get registerForSpecialConsumersUpdate(): Subject<ComponentFormData> {
    return this.triggerSpecialConsumersUpdate$;
  }

  get specialDeviceCount(): number {
    return this.noOfSpecialDevices;
  }

  set gridConnectionApplicationData(data: GridConnectionData) {
    this.gridConnectionApplicationObject = data;
  }

  initializeFormSubscriptions(): void {
    this.triggerComponentFormUpdate$ = new Subject<ComponentFormData>();
    this.triggerActionOnFormSubmit$ = new Subject<boolean>();
    this.triggerSpecialConsumersUpdate$ = new Subject<ComponentFormData>();
  }

  manageSpecialConsumersFormData(data: ComponentFormData): void {
    if (data.index > -1) {
      this.noOfSpecialDevices += 1;
      this.triggerSpecialConsumersUpdate$.next(data);
    } else {
      this.noOfSpecialDevices = 0;
    }
  }

  actionOnFormSubmit(): void {
    this.triggerActionOnFormSubmit$.next(true);
  }

  parseDataForSubmission(formType: number, formData: FormGroup): any {
    return formData.value;
  }

  getFormData(): any {
    return {
      baseData: this.baseData,
      gasConnection: this.gasConnectionData,
      heatingConnection: this.heatingConnectionData,
      powerConnection: this.powerConnectionData,
      waterConnection: this.waterConnectionData,
    };
  }

  getGridConnectionApplication(): Observable<GetGridConnectionObject> {
    const applicationId = this.localStorageService.getItem('application_id');

    return this.communicationService.getData(`${UrlSegments.GridConnectionApplication}${applicationId}`);
  }

  getCalculatedPrice(): Observable<GetGridConnectionObject> {
    const applicationId = this.localStorageService.getItem('application_id');
    return this.communicationService.updateData(`${UrlSegments.DeterminePrice}${applicationId}`, null);
  }

  prepareBaseData(data: any): GridConnectionData {
    const locationData = this.localStorageService.getItem('location_data');
    const dataObject: GridConnectionData = {
      has_residential_unit: data.residentialUnits ? '1' : '0',
      has_commercial_unit: data.business ? '1' : '0',
      residential_unit_count: data.residentialUnits ? data.residentialUnitsCount : '',
      commercial_unit_count: data.business ? data.commercialUnitsCount : '',
      public_facility: data.publicationInstitutionCheckbox ? '1' : '0',
      earthwork_own_contribution: data.earthworkContributionCheckbox ? '1' : '0',
      generation_unit_included: data.generatingPlantCheckbox ? '1' : '0',
      planned_gen_larger_30: data.plannedOutputCheckbox ? '1' : '0',
      site_power_required: data.siteElectricityCheckbox ? '1' : '0',
      site_water_required: data.siteWaterCheckbox ? '1' : '0',
      sws_feedin_available: data.feedInSystemCheckbox ? '1' : '0',
      sws_wallbox_available: data.wallboxCheckbox ? '1' : '0',
      sws_interest_in_fiberopt: data.interestInFibreCheckbox ? '1' : '0',
      development_area: locationData && locationData.newDevArea ? '1' : '0',
      ...(this.environmentService.isDecommissioningOfGridConnection && {
        sk_swkn_shutdown_reason: data.shutDownReason,
        swkn_shutdown_reason_details: data.otherShutDownReason,
      }),
      ...(this.environmentService.trenchLengthOver30 && { over_30_meter: data.connectionLineCheckbox ? '1' : '0' }),
      ...(this.environmentService.trenchLengthFixed && {
        sws_trench_length_total: data.cableLengthTotal,
        sws_trench_length_unpaved: data.cableLengthFixed,
      }),
      ...(this.environmentService.showTelecomConnectionCheckbox && { swkn_telecom_con: data.telecomConnectionCheckbox ? '1' : '0' }),
      ...(this.environmentService.showWidebandConnectionCheckbox && { swkn_wideband_con: data.widebandConnectionCheckbox ? '1' : '0' }),
      ...(this.environmentService.otherInquiriesSection && {
        sws_with_basement: data.swsWithBasement,
        sws_con_lead_basement: data.swsConLeadBasement,
        sws_waterproof_basement: data.swsWaterProofBasement,
        sws_con_lead_main_building: data.swsConLeadMainBuilding,
        sws_third_party_affected: data.swsThirdPartyAffected,
        sws_plot_size: typeof data.swsPlotSize === 'string' ? data.swsPlotSize.replace(/\./g, '') : data.swsPlotSize,
      }),
      ...(this.environmentService.showReasonForReinforcement && { reason_for_reinforcement: data.reasonForReinforcement }),
      ...(this.environmentService.showConnectionOptions && { connection_type: data.connectionsType }),
    };

    if (this.environmentService.showHALengthsSection && this.currentFlowType === InstallationFlowType.MainsConnection) {
      dataObject.ffb_basement_available = data.basementAvailable ? '1' : '0';
      dataObject.ffb_serviceroom_available = data.houseConnectionRoomAvailable ? '1' : '0';
      dataObject.ffb_ha_length_private_prop = data.haLengths;
    }

    return dataObject;
  }

  prepareGasConnectionData(data: any): GasGridConnectionData {
    return {
      required_total_power: data.handoverLocationPower || '',
      heating_gas: data.heatingGasCheckbox ? '1' : '0',
      kitchen_gas: data.cookingGasCheckbox ? '1' : '0',
      bhkw: data.bhkwCheckbox ? '1' : '0',
      process_gas: data.processGasCheckbox ? '1' : '0',
      required_pressure: data.desiredTransferPressure || '',
      resp_technician_ident: '',
      resp_technician_company: '',
      resp_technician_phone: '',
      disposal_of_oil_tank: data.disposalOfOilTank ? '1' : '0',
      size_of_tank: data.sizeOfTank || '',
      boiler_output_heating_gas: data.boilerOutputHeatingGas || '',
      water_treatment: data.waterTreatment ? '1' : '0',
      water_protection_area: data.waterProtectionArea ? '1' : '0',
    };
  }

  prepareHeatingConnectionData(data: any): HeatingGridConnectionData {
    return {
      ...(!this.environmentService.showOnlyPeakFlow && { heatingload: data.heatingLoad || '' }),
      ...(this.environmentService.showOnlyPeakFlow && { peakflow: data.peakFlow || '' }),
      sk_districtheat_distribution_type: data.supplyType || null,
      sk_districtheat_heating_type: data.heatingSystemType || null,
      drinking_water: data.dhwheating ? '1' : '0',
      compactstation: data.compactStation ? '1' : '0',
      individual_billing: data.singleBillCheckbox ? '1' : '0',
      sk_hotwater_prep_type: data.typeOf || null,
      smoke_alarm_service: data.smokeAlarmServiceCheckbox ? '1' : '0',
      sk_variable_costs: data.variableCostsShare || '0',
      resp_technician_ident: '',
      resp_technician_company: '',
      resp_technician_phone: '',
    };
  }

  preparePowerConnectionData(data: any): PowerGridConnectionData {
    const dataObject: PowerGridConnectionData = {
      resp_technician_ident: data.tax_id || '',
      resp_technician_company: data.company_name || '',
      resp_technician_phone: data.telephone || '',
      installation_location_type: data.installationLocationType,
      installation_location_type_other: data.installationLocationTypeOther,
      оffer_earthworks_on_private_property: data.offerEarthworksOnPrivateProperty,
      power_connection_heating_type: data.powerConnectionHeatingType,
      power_connection_heating_type_other: data.powerConnectionHeatingTypeOther,
      is_temp_connection_less30_required: data.requiredTempConnectionLess30,
      technician_bdew_id: data.technicianBdewId,
      earthwork_area_name: data.earthworkAreaName || '',
      sk_swkn_required_fuse_power: data.SkSwknRequiredFusePower || '',
    };

    if (
      this.environmentService.showTotalOutputField ||
      (!this.environmentService.showTotalOutputField &&
        !(
          this.currentFlowType === InstallationFlowType.MainsConnection ||
          this.currentFlowType === InstallationFlowType.ChangeHouseConnection
        ))
    ) {
      dataObject.required_total_power = data.totalOutput?.replace(',', '.') || '';
    }

    if (
      this.environmentService.showFuseStrengthDropdown &&
      (this.currentFlowType === InstallationFlowType.MainsConnection || this.currentFlowType === InstallationFlowType.ChangeHouseConnection)
    ) {
      dataObject.sk_swkn_required_fuse_power = data.requiredFusePower || '';
    }

    if (this.environmentService.powerConnectionShowEquipmentAvailableCheckboxes) {
      dataObject.pump_available = data.pumpAvailable;
      dataObject.crane_available = data.craneAvailable;
    }

    if (this.environmentService.showOtherCommoditiesForInstallation) {
      dataObject.other_commodities_for_installation_gas = data.otherCommoditiesForInstallationGas;
      dataObject.other_commodities_for_installation_heating = data.otherCommoditiesForInstallationHeating;
      dataObject.other_commodities_for_installation_water = data.otherCommoditiesForInstallationWater;
      dataObject.other_commodities_for_installation_other = data.otherCommoditiesForInstallationOther;
      dataObject.other_commodities_for_installation_other_input = data.otherCommoditiesForInstallationOtherInput;
      dataObject.temp_power_report_confirmed = data.tempPowerReportConfirmed;
    }

    return dataObject;
  }

  prepareSpecialConsumerData(data: any): PowerSpecialConsumerData {
    const currentFlow = this.localStorageService.getItem('installation_flow');
    const requestData: PowerSpecialConsumerData = {
      consumer_count: data.count,
      power: data.power,
      description: data.description,
      other_type_details: data.otherType || '',
      module_type: data.module_type,
      is_controllable_facility: data.is_controllable_facility != null ? (data.is_controllable_facility ? 'true' : 'false') : 'false',
      is_public_facility: data.is_public_facility != null ? (data.is_public_facility ? 'true' : 'false') : 'false',
      zerez_id: data.zerez_id,
      ski_key: data.ski_key,
      date_of_commissioning: data.date_of_commissioning,
    };

    if (currentFlow === InstallationFlowType.TemporaryConnection || currentFlow === InstallationFlowType.ChangeHouseConnection) {
      requestData.sk_tmp_specialconsumer_type = data.device;
    } else if (currentFlow === InstallationFlowType.MainsConnection) {
      requestData.sk_specialconsumer_type = data.device;
    }

    return requestData;
  }

  prepareWaterConnectionData(data: any): WaterGridConnectionData {
    const dataObject: WaterGridConnectionData = {
      peakflow: data.peakFlow || '',
      specialservice: data.specialService ? '1' : '0',
      specialservice_desc: data.description || '',
      firewater: data.fireWater ? '1' : '0',
      firewater_type: data.institutionType || '',
      individual_billing: data.singleBillCheckbox ? '1' : '0',
      sk_hotwater_prep_type: data.waterHeatingType || '',
      resp_technician_ident: '',
      resp_technician_company: '',
      resp_technician_phone: '',
    };
    if (data.swknParcelSize) {
      dataObject.swkn_parcel_size = data.swknParcelSize;
    }
    if (data.swknFirewaterPeakflow) {
      dataObject.swkn_firewater_peakflow = data.swknFirewaterPeakflow;
    }
    return dataObject;
  }

  parseApplicationDataIntoForm(selectedFacilities: FormSubsectionsVisibility): any {
    this.selectedHousingType = this.localStorageService.getItem('selected_building_type') || [];
    this.selectedFacilities = selectedFacilities || this.selectedFacilities;

    switch (this.currentFlowType) {
      case InstallationFlowType.MainsConnection:
        this.parseGridConnectionBaseData();
        this.parseGridConnectionGasData();
        this.parseGridConnectionHeatingData();
        this.parseGridConnectionPowerData();
        this.parseGridConnectionWaterData();
        break;
      case InstallationFlowType.FeedInSystem:
        this.parseFeedInData();
        break;
      case InstallationFlowType.TemporaryConnection:
        this.parseTempPowerConnectionData();
        this.parseTempWaterConnectionData();
        break;
      case InstallationFlowType.ChangeHouseConnection:
        this.parseChangeHouseConnectionBaseData();
        this.parseGridConnectionGasData();
        this.parseGridConnectionHeatingData();
        this.parseChangeHouseConnectionPowerData();
        this.parseGridConnectionWaterData();
        this.parseGridConnectionBaseData();
        break;
      case InstallationFlowType.ConversionToGas:
        this.parseGridConnectionBaseData();
        this.parseGridConnectionGasData();
        this.parseGridConnectionHeatingData();
        break;
    }
  }

  parseGridConnectionBaseData(): void {
    if (this.currentFlowType === InstallationFlowType.ConversionToGas) {
      this.baseData = {
        siteElectricityCheckbox: this.gridConnectionApplicationObject.site_power_required === '1',
        siteWaterCheckbox: this.gridConnectionApplicationObject.site_water_required === '1',
        residentialUnits: this.gridConnectionApplicationObject.has_residential_unit === '1',
        residentialUnitsCount: this.gridConnectionApplicationObject.residential_unit_count,
        business: this.gridConnectionApplicationObject.has_commercial_unit === '1',
        commercialUnitsCount: this.gridConnectionApplicationObject.commercial_unit_count,
      };
      if (this.environmentService.trenchLengthFixed) {
        this.baseData.cableLengthTotal = this.gridConnectionApplicationObject.sws_trench_length_total;
        this.baseData.cableLengthFixed = this.gridConnectionApplicationObject.sws_trench_length_unpaved;
      }
    } else {
      if (this.selectedHousingType.length) {
        switch (this.selectedHousingType[0].index) {
          case 0:
          case 9:
            this.baseData = {
              generatingPlantCheckbox: this.gridConnectionApplicationObject.generation_unit_included === '1',
              plannedOutputCheckbox: this.gridConnectionApplicationObject.planned_gen_larger_30 === '1',
              siteElectricityCheckbox: this.gridConnectionApplicationObject.site_power_required === '1',
              siteWaterCheckbox: this.gridConnectionApplicationObject.site_water_required === '1',
              earthworkContributionCheckbox: this.gridConnectionApplicationObject.earthwork_own_contribution === '1',
            };
            if (this.environmentService.trenchLengthOver30) {
              this.baseData.connectionLineCheckbox = this.gridConnectionApplicationObject.over_30_meter === '1';
            }
            if (this.environmentService.trenchLengthFixed) {
              this.baseData.cableLengthTotal = this.gridConnectionApplicationObject.sws_trench_length_total;
              this.baseData.cableLengthFixed = this.gridConnectionApplicationObject.sws_trench_length_unpaved;
            }
            if (this.environmentService.showTelecomConnectionCheckbox) {
              this.baseData.telecomConnectionCheckbox = this.gridConnectionApplicationObject.swkn_telecom_con === '1';
            }
            if (this.environmentService.showWidebandConnectionCheckbox) {
              this.baseData.widebandConnectionCheckbox = this.gridConnectionApplicationObject.swkn_wideband_con === '1';
            }
            break;
          case 1:
            this.baseData = {
              generatingPlantCheckbox: this.gridConnectionApplicationObject.generation_unit_included === '1',
              plannedOutputCheckbox: this.gridConnectionApplicationObject.planned_gen_larger_30 === '1',
              siteElectricityCheckbox: this.gridConnectionApplicationObject.site_power_required === '1',
              siteWaterCheckbox: this.gridConnectionApplicationObject.site_water_required === '1',
              residentialUnitsCount: this.gridConnectionApplicationObject.residential_unit_count,
              commercialUnitsCount: this.gridConnectionApplicationObject.commercial_unit_count,
              publicationInstitutionCheckbox: this.gridConnectionApplicationObject.public_facility === '1',
              earthworkContributionCheckbox: this.gridConnectionApplicationObject.earthwork_own_contribution === '1',
            };
            if (this.environmentService.trenchLengthOver30) {
              this.baseData.connectionLineCheckbox = this.gridConnectionApplicationObject.over_30_meter === '1';
            }
            if (this.environmentService.trenchLengthFixed) {
              this.baseData.cableLengthTotal = this.gridConnectionApplicationObject.sws_trench_length_total;
              this.baseData.cableLengthFixed = this.gridConnectionApplicationObject.sws_trench_length_unpaved;
            }
            if (this.environmentService.showTelecomConnectionCheckbox) {
              this.baseData.telecomConnectionCheckbox = this.gridConnectionApplicationObject.swkn_telecom_con === '1';
            }
            if (this.environmentService.showWidebandConnectionCheckbox) {
              this.baseData.widebandConnectionCheckbox = this.gridConnectionApplicationObject.swkn_wideband_con === '1';
            }
            break;
          case 2:
            this.baseData = {
              generatingPlantCheckbox: this.gridConnectionApplicationObject.generation_unit_included === '1',
              plannedOutputCheckbox: this.gridConnectionApplicationObject.planned_gen_larger_30 === '1',
              siteElectricityCheckbox: this.gridConnectionApplicationObject.site_power_required === '1',
              siteWaterCheckbox: this.gridConnectionApplicationObject.site_water_required === '1',
              residentialUnitsCount: this.gridConnectionApplicationObject.residential_unit_count,
              commercialUnitsCount: this.gridConnectionApplicationObject.commercial_unit_count,
              publicationInstitutionCheckbox: this.gridConnectionApplicationObject.public_facility === '1',
            };
            break;
        }
      }
    }
    if (this.currentFlowType === InstallationFlowType.MainsConnection) {
      this.baseData.feedInSystemCheckbox = this.gridConnectionApplicationObject.sws_feedin_available === '1';
      this.baseData.wallboxCheckbox = this.gridConnectionApplicationObject.sws_wallbox_available === '1';
      this.baseData.interestInFibreCheckbox = this.gridConnectionApplicationObject.sws_interest_in_fiberopt === '1';
    }
    if (this.environmentService.otherInquiriesSection) {
      this.baseData.swsWithBasement = this.gridConnectionApplicationObject.sws_with_basement;
      this.baseData.swsConLeadBasement = this.gridConnectionApplicationObject.sws_con_lead_basement;
      this.baseData.swsWaterProofBasement = this.gridConnectionApplicationObject.sws_waterproof_basement;
      this.baseData.swsConLeadMainBuilding = this.gridConnectionApplicationObject.sws_con_lead_main_building;
      this.baseData.swsThirdPartyAffected = this.gridConnectionApplicationObject.sws_third_party_affected;
      this.baseData.swsPlotSize = this.gridConnectionApplicationObject.sws_plot_size;
      this.baseData.connectionType = this.gridConnectionApplicationObject.connection_type;
    }
    if (this.environmentService.showHALengthsSection && this.currentFlowType === InstallationFlowType.MainsConnection) {
      this.baseData.haLengths = this.gridConnectionApplicationObject.ffb_ha_length_private_prop;
      this.baseData.basementAvailable = this.gridConnectionApplicationObject.ffb_basement_available === '1';
      this.baseData.houseConnectionRoomAvailable = this.gridConnectionApplicationObject.ffb_serviceroom_available === '1';
    }
    if (this.currentFlowType === InstallationFlowType.ChangeHouseConnection) {
      if (this.environmentService.trenchLengthFixed) {
        this.baseData.cableLengthTotal = this.gridConnectionApplicationObject.sws_trench_length_total;
        this.baseData.cableLengthFixed = this.gridConnectionApplicationObject.sws_trench_length_unpaved;
      }
      if (this.environmentService.showReasonForReinforcement) {
        this.baseData.reasonForReinforcement = this.gridConnectionApplicationObject.reason_for_reinforcement;
      }

      this.baseData.residentialUnits = this.gridConnectionApplicationObject.has_residential_unit === '1';
      this.baseData.residentialUnitsCount = this.gridConnectionApplicationObject.residential_unit_count;
      this.baseData.business = this.gridConnectionApplicationObject.has_commercial_unit === '1';
      this.baseData.commercialUnitsCount = this.gridConnectionApplicationObject.commercial_unit_count;
    }
  }

  parseGridConnectionGasData(): void {
    if (this.selectedFacilities.gas) {
      if (
        this.currentFlowType === InstallationFlowType.ConversionToGas ||
        this.currentFlowType === InstallationFlowType.ChangeHouseConnection
      ) {
        this.gasConnectionData = {
          bhkwCheckbox: this.gridConnectionApplicationObject.gasgridcon.bhkw === '1',
          cookingGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.kitchen_gas === '1',
          desiredTransferPressure: this.gridConnectionApplicationObject.gasgridcon.required_pressure,
          heatingGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.heating_gas === '1',
          handoverLocationPower: this.gridConnectionApplicationObject.gasgridcon.required_total_power,
          processGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.process_gas === '1',
          id: this.gridConnectionApplicationObject.gasgridcon.gridcon_id,
        };
      } else {
        if (this.selectedHousingType.length) {
          switch (this.selectedHousingType[0].index) {
            case 0:
            case 9:
              this.gasConnectionData = {
                bhkwCheckbox: this.gridConnectionApplicationObject.gasgridcon.bhkw === '1',
                cookingGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.kitchen_gas === '1',
                heatingGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.heating_gas === '1',
                handoverLocationPower: this.gridConnectionApplicationObject.gasgridcon.required_total_power,
                id: this.gridConnectionApplicationObject.gasgridcon.gridcon_id,
              };
              break;
            case 1:
              this.gasConnectionData = {
                bhkwCheckbox: this.gridConnectionApplicationObject.gasgridcon.bhkw === '1',
                cookingGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.kitchen_gas === '1',
                heatingGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.heating_gas === '1',
                handoverLocationPower: this.gridConnectionApplicationObject.gasgridcon.required_total_power,
                processGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.process_gas === '1',
                id: this.gridConnectionApplicationObject.gasgridcon.gridcon_id,
              };
              break;
            case 2:
              this.gasConnectionData = {
                bhkwCheckbox: this.gridConnectionApplicationObject.gasgridcon.bhkw === '1',
                cookingGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.kitchen_gas === '1',
                desiredTransferPressure: this.gridConnectionApplicationObject.gasgridcon.required_pressure,
                heatingGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.heating_gas === '1',
                handoverLocationPower: this.gridConnectionApplicationObject.gasgridcon.required_total_power,
                processGasCheckbox: this.gridConnectionApplicationObject.gasgridcon.process_gas === '1',
                id: this.gridConnectionApplicationObject.gasgridcon.gridcon_id,
              };
              break;
          }
        }
      }
    }
  }

  parseGridConnectionHeatingData(): void {
    if (this.selectedFacilities.heating) {
      if (
        this.currentFlowType === InstallationFlowType.ConversionToGas ||
        this.currentFlowType === InstallationFlowType.ChangeHouseConnection
      ) {
        this.heatingConnectionData = {
          dhwheating: this.gridConnectionApplicationObject.districtheatgridcon.drinking_water === '1',
          heatingLoad: this.gridConnectionApplicationObject.districtheatgridcon.heatingload,
          heatingSystemType: this.gridConnectionApplicationObject.districtheatgridcon.sk_districtheat_heating_type,
          supplyType: this.gridConnectionApplicationObject.districtheatgridcon.sk_districtheat_distribution_type,
          singleBillCheckbox: this.gridConnectionApplicationObject.districtheatgridcon.individual_billing === '1',
          typeOf: this.gridConnectionApplicationObject.districtheatgridcon.sk_hotwater_prep_type,
          variableCostsShare: this.gridConnectionApplicationObject.districtheatgridcon.sk_variable_costs,
          smokeAlarmServiceCheckbox: this.gridConnectionApplicationObject.districtheatgridcon.smoke_alarm_service === '1',
          id: this.gridConnectionApplicationObject.districtheatgridcon.gridcon_id,
        };
      } else {
        if (this.selectedHousingType.length) {
          switch (this.selectedHousingType[0].index) {
            case 0:
            case 9:
              this.heatingConnectionData = {
                dhwheating: this.gridConnectionApplicationObject.districtheatgridcon.drinking_water === '1',
                heatingLoad: this.gridConnectionApplicationObject.districtheatgridcon.heatingload,
                peakFlow: this.gridConnectionApplicationObject.districtheatgridcon.peakflow,
                heatingSystemType: this.gridConnectionApplicationObject.districtheatgridcon.sk_districtheat_heating_type,
                supplyType: this.gridConnectionApplicationObject.districtheatgridcon.sk_districtheat_distribution_type,
                compactStation: this.gridConnectionApplicationObject.districtheatgridcon.compactstation === '1',
                id: this.gridConnectionApplicationObject.districtheatgridcon.gridcon_id,
                typeOf: this.gridConnectionApplicationObject.districtheatgridcon.sk_hotwater_prep_type,
              };
              break;
            case 1:
              this.heatingConnectionData = {
                dhwheating: this.gridConnectionApplicationObject.districtheatgridcon.drinking_water === '1',
                heatingLoad: this.gridConnectionApplicationObject.districtheatgridcon.heatingload,
                peakFlow: this.gridConnectionApplicationObject.districtheatgridcon.peakflow,
                heatingSystemType: this.gridConnectionApplicationObject.districtheatgridcon.sk_districtheat_heating_type,
                compactStation: this.gridConnectionApplicationObject.districtheatgridcon.compactstation === '1',
                singleBillCheckbox: this.gridConnectionApplicationObject.districtheatgridcon.individual_billing === '1',
                smokeAlarmServiceCheckbox: this.gridConnectionApplicationObject.districtheatgridcon.smoke_alarm_service === '1',
                supplyType: this.gridConnectionApplicationObject.districtheatgridcon.sk_districtheat_distribution_type,
                typeOf: this.gridConnectionApplicationObject.districtheatgridcon.sk_hotwater_prep_type,
                variableCostsShare: this.gridConnectionApplicationObject.districtheatgridcon.sk_variable_costs,
                id: this.gridConnectionApplicationObject.districtheatgridcon.gridcon_id,
              };
              break;
            case 2:
              this.heatingConnectionData = {
                dhwheating: this.gridConnectionApplicationObject.districtheatgridcon.drinking_water === '1',
                heatingLoad: this.gridConnectionApplicationObject.districtheatgridcon.heatingload,
                peakFlow: this.gridConnectionApplicationObject.districtheatgridcon.peakflow,
                heatingSystemType: this.gridConnectionApplicationObject.districtheatgridcon.sk_districtheat_heating_type,
                supplyType: this.gridConnectionApplicationObject.districtheatgridcon.sk_districtheat_distribution_type,
                singleBillCheckbox: this.gridConnectionApplicationObject.districtheatgridcon.individual_billing === '1',
                typeOf: this.gridConnectionApplicationObject.districtheatgridcon.sk_hotwater_prep_type,
                variableCostsShare: this.gridConnectionApplicationObject.districtheatgridcon.sk_variable_costs,
                smokeAlarmServiceCheckbox: this.gridConnectionApplicationObject.districtheatgridcon.smoke_alarm_service === '1',
                id: this.gridConnectionApplicationObject.districtheatgridcon.gridcon_id,
              };
              break;
          }
        }
      }
    }
  }

  parseGridConnectionPowerData(): void {
    if (this.selectedFacilities.power) {
      this.powerConnectionData = {
        specialDevice: [],
        totalOutput: this.gridConnectionApplicationObject.powergridcon.required_total_power || '',
        requiredFusePower: this.gridConnectionApplicationObject.powergridcon.sk_swkn_required_fuse_power || '',
        id: this.gridConnectionApplicationObject.powergridcon.gridcon_id,
        installationLocationType: this.gridConnectionApplicationObject.powergridcon.installation_location_type,
        installationLocationTypeOther: this.gridConnectionApplicationObject.powergridcon.installation_location_type_other,
        offerEarthworksOnPrivateProperty: JSON.parse(
          this.gridConnectionApplicationObject.powergridcon.оffer_earthworks_on_private_property?.toString() ?? null
        ),
        powerConnectionHeatingType: this.gridConnectionApplicationObject.powergridcon.power_connection_heating_type,
        powerConnectionHeatingTypeOther: this.gridConnectionApplicationObject.powergridcon.power_connection_heating_type_other,
        requiredTempConnectionLess30:
          this.gridConnectionApplicationObject.powergridcon.is_temp_connection_less30_required?.toString() === 'true',
        technicianBdewId: this.gridConnectionApplicationObject.powergridcon.technician_bdew_id,
        sk_power_connection_type: this.gridConnectionApplicationObject.sk_power_connection_type,
        earthworkAreaName: this.gridConnectionApplicationObject.powergridcon.earthwork_area_name || '',
        otherCommoditiesForInstallationGas:
          this.gridConnectionApplicationObject.powergridcon.other_commodities_for_installation_gas?.toString() === 'true',
        otherCommoditiesForInstallationHeating:
          this.gridConnectionApplicationObject.powergridcon.other_commodities_for_installation_heating?.toString() === 'true',
        otherCommoditiesForInstallationWater:
          this.gridConnectionApplicationObject.powergridcon.other_commodities_for_installation_water?.toString() === 'true',
        otherCommoditiesForInstallationOther:
          this.gridConnectionApplicationObject.powergridcon.other_commodities_for_installation_other?.toString() === 'true',
        otherCommoditiesForInstallationOtherInput:
          this.gridConnectionApplicationObject.powergridcon.other_commodities_for_installation_other_input || '',
        tempPowerReportConfirmed: this.gridConnectionApplicationObject.powergridcon.temp_power_report_confirmed?.toString() === 'true',
      };

      if (this.gridConnectionApplicationObject.powergridcon.specialconsumer) {
        this.gridConnectionApplicationObject.powergridcon.specialconsumer.forEach(item => {
          this.powerConnectionData.specialDevice.push({
            device: item.sk_specialconsumer_type,
            description: item.description,
            count: item.consumer_count,
            power: item.power,
            id: item.specialconsumer_id,
            otherType: item.other_type_details,
            module_type: item.module_type,
            is_controllable_facility: item.is_controllable_facility === 'true',
            is_public_facility: item.is_public_facility === 'true',
            zerez_id: item.zerez_id,
            ski_key: item.ski_key,
            date_of_commissioning: item.date_of_commissioning,
          });
        });
      }
    }
  }

  parseGridConnectionWaterData(): void {
    if (this.selectedFacilities.water) {
      if (
        this.currentFlowType === InstallationFlowType.ConversionToGas ||
        this.currentFlowType === InstallationFlowType.ChangeHouseConnection
      ) {
        this.waterConnectionData = {
          description: this.gridConnectionApplicationObject.watergridcon.specialservice_desc,
          fireWater: this.gridConnectionApplicationObject.watergridcon.firewater,
          institutionType: this.gridConnectionApplicationObject.watergridcon.firewater_type,
          peakFlow: this.gridConnectionApplicationObject.watergridcon.peakflow,
          specialService: this.gridConnectionApplicationObject.watergridcon.specialservice === '1',
          singleBillCheckbox: this.gridConnectionApplicationObject.watergridcon.individual_billing === '1',
          waterHeatingType: this.gridConnectionApplicationObject.watergridcon.sk_hotwater_prep_type,
          id: this.gridConnectionApplicationObject.watergridcon.gridcon_id,
          swknParcelSize: this.gridConnectionApplicationObject.watergridcon.swkn_parcel_size,
          swknFirewaterPeakflow: this.gridConnectionApplicationObject.watergridcon.swkn_firewater_peakflow,
        };
      } else {
        if (this.selectedHousingType.length) {
          switch (this.selectedHousingType[0].index) {
            case 0:
            case 9:
              this.waterConnectionData = {
                description: this.gridConnectionApplicationObject.watergridcon.specialservice_desc,
                fireWater: this.gridConnectionApplicationObject.watergridcon.firewater,
                institutionType: this.gridConnectionApplicationObject.watergridcon.firewater_type,
                peakFlow: this.gridConnectionApplicationObject.watergridcon.peakflow,
                specialService: this.gridConnectionApplicationObject.watergridcon.specialservice === '1',
                id: this.gridConnectionApplicationObject.watergridcon.gridcon_id,
                swknParcelSize: this.gridConnectionApplicationObject.watergridcon.swkn_parcel_size,
                swknFirewaterPeakflow: this.gridConnectionApplicationObject.watergridcon.swkn_firewater_peakflow,
              };
              break;
            case 1:
              this.waterConnectionData = {
                description: this.gridConnectionApplicationObject.watergridcon.specialservice_desc,
                fireWater: this.gridConnectionApplicationObject.watergridcon.firewater,
                institutionType: this.gridConnectionApplicationObject.watergridcon.firewater_type,
                peakFlow: this.gridConnectionApplicationObject.watergridcon.peakflow,
                specialService: this.gridConnectionApplicationObject.watergridcon.specialservice === '1',
                singleBillCheckbox: this.gridConnectionApplicationObject.watergridcon.individual_billing === '1',
                waterHeatingType: this.gridConnectionApplicationObject.watergridcon.sk_hotwater_prep_type,
                id: this.gridConnectionApplicationObject.watergridcon.gridcon_id,
                swknParcelSize: this.gridConnectionApplicationObject.watergridcon.swkn_parcel_size,
                swknFirewaterPeakflow: this.gridConnectionApplicationObject.watergridcon.swkn_firewater_peakflow,
              };
              break;
            case 2:
              this.waterConnectionData = {
                description: this.gridConnectionApplicationObject.watergridcon.specialservice_desc,
                fireWater: this.gridConnectionApplicationObject.watergridcon.firewater,
                institutionType: this.gridConnectionApplicationObject.watergridcon.firewater_type,
                peakFlow: this.gridConnectionApplicationObject.watergridcon.peakflow,
                specialService: this.gridConnectionApplicationObject.watergridcon.specialservice === '1',
                singleBillCheckbox: this.gridConnectionApplicationObject.watergridcon.individual_billing === '1',
                waterHeatingType: this.gridConnectionApplicationObject.watergridcon.sk_hotwater_prep_type,
                id: this.gridConnectionApplicationObject.watergridcon.gridcon_id,
                swknParcelSize: this.gridConnectionApplicationObject.watergridcon.swkn_parcel_size,
                swknFirewaterPeakflow: this.gridConnectionApplicationObject.watergridcon.swkn_firewater_peakflow,
              };
              break;
          }
        }
      }
    }
  }

  parseTempPowerConnectionData(): void {
    if (this.selectedFacilities.power) {
      this.powerConnectionData = {
        specialDevice: [],
        totalOutput: this.gridConnectionApplicationObject.powergridcon.required_total_power || '',
        id: this.gridConnectionApplicationObject.powergridcon.gridcon_id,
      };

      if (this.gridConnectionApplicationObject.powergridcon.specialconsumer) {
        this.gridConnectionApplicationObject.powergridcon.specialconsumer.forEach(item => {
          this.powerConnectionData.specialDevice.push({
            device: item.sk_tmp_specialconsumer_type,
            count: item.consumer_count,
            power: item.power,
            id: item.specialconsumer_id,
            zerez_id: item.zerez_id,
            ski_key: item.ski_key,
            date_of_commissioning: item.date_of_commissioning,
          });
        });
      }
    }
  }

  parseChangeHouseConnectionPowerData(): void {
    if (this.selectedFacilities.power) {
      this.powerConnectionData = {
        specialDevice: [],
        totalOutput: this.gridConnectionApplicationObject.powergridcon.required_total_power || '',
        requiredFusePower: this.gridConnectionApplicationObject.powergridcon.sk_swkn_required_fuse_power || '',
        id: this.gridConnectionApplicationObject.powergridcon.gridcon_id,
      };

      if (this.gridConnectionApplicationObject.powergridcon.specialconsumer) {
        this.gridConnectionApplicationObject.powergridcon.specialconsumer.forEach(item => {
          this.powerConnectionData.specialDevice.push({
            device: item.sk_tmp_specialconsumer_type,
            count: item.consumer_count,
            power: item.power,
            id: item.specialconsumer_id,
            otherType: item.other_type_details,
            description: item.description,
            is_controllable_facility: item.is_controllable_facility === 'true',
            is_public_facility: item.is_public_facility === 'true',
            zerez_id: item.zerez_id,
          });
        });
      }
    }
  }

  parseTempWaterConnectionData(): void {
    if (this.selectedFacilities.water) {
      this.waterConnectionData = {
        peakFlow: this.gridConnectionApplicationObject.watergridcon.peakflow,
        id: this.gridConnectionApplicationObject.watergridcon.gridcon_id,
      };
    }
  }

  parseChangeHouseConnectionBaseData(): void {
    this.baseData = {
      generatingPlantCheckbox: this.gridConnectionApplicationObject.generation_unit_included === '1',
      plannedOutputCheckbox: this.gridConnectionApplicationObject.planned_gen_larger_30 === '1',
      siteElectricityCheckbox: this.gridConnectionApplicationObject.site_power_required === '1',
      siteWaterCheckbox: this.gridConnectionApplicationObject.site_water_required === '1',
      residentialUnitsCount: this.gridConnectionApplicationObject.residential_unit_count,
      commercialUnitsCount: this.gridConnectionApplicationObject.commercial_unit_count,
      publicationInstitutionCheckbox: this.gridConnectionApplicationObject.public_facility === '1',
      connectionLineCheckbox: this.gridConnectionApplicationObject.over_30_meter === '1',
      earthworkContributionCheckbox: this.gridConnectionApplicationObject.earthwork_own_contribution === '1',
      residentialUnits: this.gridConnectionApplicationObject.has_residential_unit === '1',
      business: this.gridConnectionApplicationObject.has_commercial_unit === '1',
      totalOutput: this.gridConnectionApplicationObject.powergridcon.required_total_power,
      tax_id: this.gridConnectionApplicationObject.powergridcon.resp_technician_ident,
      company_name: this.gridConnectionApplicationObject.powergridcon.resp_technician_company,
      telephone: this.gridConnectionApplicationObject.powergridcon.resp_technician_phone,
      peakFlow: this.gridConnectionApplicationObject.watergridcon.peakflow,
      id: this.gridConnectionApplicationObject.powergridcon.gridcon_id,
      requiredFusePower: this.gridConnectionApplicationObject.sk_swkn_required_fuse_power,
      specialDevices: [],
    };

    const specialConsumer = this.gridConnectionApplicationObject.powergridcon.specialconsumer || [];
    if (specialConsumer.length) {
      specialConsumer.forEach(item => {
        this.baseData.specialDevices.push({
          device: item.sk_tmp_specialconsumer_type,
          count: item.consumer_count,
          power: item.power,
          description: item.description,
          id: item.specialconsumer_id,
        });
      });
    }
    if (this.environmentService.isDecommissioningOfGridConnection) {
      this.baseData.shutDownReason = this.gridConnectionApplicationObject.sk_swkn_shutdown_reason;
      this.baseData.otherShutDownReason = this.gridConnectionApplicationObject.swkn_shutdown_reason_details;
    }
  }

  parseFeedInData(): void {
    // const systemTypeData = this.installationApplicationObject.systemTypeData;
    // const systemOperationData = this.installationApplicationObject.systemOperationData;
    // const measuringConceptData = this.installationApplicationObject.measuringConceptData;

    this.feedInData = {};
  }
}
